import { TaxPaymentDisplayStatus, TaxPaymentResponse } from '@aiwyn/api';

/**
 * UI-side wrapper around `TaxPaymentResponse` that pre-computes display values
 * (currency-formatted amount, action visibility, badge styling, etc.) so the
 * template stays declarative.
 *
 * The shape intentionally re-exports the response fields so it can be passed
 * directly to `mat-table` without an extra adapter layer.
 */
export interface TaxPaymentUi extends TaxPaymentResponse {
  /** Whether the Pay Now button should be rendered for this row. */
  canPayNow: boolean;
  /** Major-currency-units amount, derived from `amountCents`. */
  amount: number;
  /** Human-readable status label (e.g. "Due Soon"). */
  statusLabel: string;
  /** Tailwind/SCSS modifier suffix applied via `badge--{badgeVariant}`. */
  badgeVariant: 'primary' | 'warning' | 'error' | 'success' | 'info' | 'muted';
  /** Pretty tax authority label (e.g. "IRS", "California FTB"). */
  taxAuthorityLabel: string;
}

/**
 * Tax payment statuses for which the Pay Now action should be available.
 */
const PAYABLE_STATUSES: ReadonlySet<TaxPaymentDisplayStatus> = new Set<TaxPaymentDisplayStatus>([
  'OVERDUE',
  'DUE_SOON',
  'UPCOMING',
]);

const STATUS_LABELS: Record<TaxPaymentDisplayStatus, string> = {
  UPCOMING: 'Upcoming',
  DUE_SOON: 'Due Soon',
  OVERDUE: 'Overdue',
  PROCESSING: 'Processing',
  PROCESSED: 'Processed',
  CANCELLED: 'Cancelled',
  AWAITING_TAXPAYER_SETUP: 'Awaiting setup',
};

const BADGE_VARIANTS: Record<TaxPaymentDisplayStatus, TaxPaymentUi['badgeVariant']> = {
  UPCOMING: 'primary',
  DUE_SOON: 'warning',
  OVERDUE: 'error',
  PROCESSING: 'info',
  PROCESSED: 'success',
  CANCELLED: 'muted',
  AWAITING_TAXPAYER_SETUP: 'muted',
};

/**
 * Map tax authority codes to user-friendly labels. Falls back to the raw code
 * if no mapping exists so new authorities still render reasonably.
 *
 * Covers the federal code plus every `US_<2-letter>` state/territory the
 * backend can emit (all 50 states + DC), as well as the Canadian federal code
 * (`CA_CRA`) and every `CA_<2-letter>` province/territory. The four
 * `US_<state>_<agency>` variant keys (FTB/DTF/CPA) are kept so those richer
 * labels win over the generic state-name label when present. If the response
 * ever carries a server-computed `taxAuthorityLabel`, `toTaxPaymentUi` prefers
 * it over this map — but the map is the source of truth today.
 */
const TAX_AUTHORITY_LABELS: Record<string, string> = {
  US_IRS: 'IRS (Federal)',
  // Agency-specific variants — kept so they win over the plain state label.
  US_CA_FTB: 'California FTB',
  US_NY_DTF: 'New York DTF',
  US_TX_CPA: 'Texas Comptroller',
  // City-level authorities Remitian emits today.
  US_NYC: 'New York City (NYC)',
  US_PA_PHI: 'Philadelphia (PHI)',
  // All 50 states + DC, keyed by `US_<2-letter>`.
  US_AL: 'Alabama (AL)',
  US_AK: 'Alaska (AK)',
  US_AZ: 'Arizona (AZ)',
  US_AR: 'Arkansas (AR)',
  US_CA: 'California (CA)',
  US_CO: 'Colorado (CO)',
  US_CT: 'Connecticut (CT)',
  US_DE: 'Delaware (DE)',
  US_DC: 'District of Columbia (DC)',
  US_FL: 'Florida (FL)',
  US_GA: 'Georgia (GA)',
  US_HI: 'Hawaii (HI)',
  US_ID: 'Idaho (ID)',
  US_IL: 'Illinois (IL)',
  US_IN: 'Indiana (IN)',
  US_IA: 'Iowa (IA)',
  US_KS: 'Kansas (KS)',
  US_KY: 'Kentucky (KY)',
  US_LA: 'Louisiana (LA)',
  US_ME: 'Maine (ME)',
  US_MD: 'Maryland (MD)',
  US_MA: 'Massachusetts (MA)',
  US_MI: 'Michigan (MI)',
  US_MN: 'Minnesota (MN)',
  US_MS: 'Mississippi (MS)',
  US_MO: 'Missouri (MO)',
  US_MT: 'Montana (MT)',
  US_NE: 'Nebraska (NE)',
  US_NV: 'Nevada (NV)',
  US_NH: 'New Hampshire (NH)',
  US_NJ: 'New Jersey (NJ)',
  US_NM: 'New Mexico (NM)',
  US_NY: 'New York (NY)',
  US_NC: 'North Carolina (NC)',
  US_ND: 'North Dakota (ND)',
  US_OH: 'Ohio (OH)',
  US_OK: 'Oklahoma (OK)',
  US_OR: 'Oregon (OR)',
  US_PA: 'Pennsylvania (PA)',
  US_RI: 'Rhode Island (RI)',
  US_SC: 'South Carolina (SC)',
  US_SD: 'South Dakota (SD)',
  US_TN: 'Tennessee (TN)',
  US_TX: 'Texas (TX)',
  US_UT: 'Utah (UT)',
  US_VT: 'Vermont (VT)',
  US_VA: 'Virginia (VA)',
  US_WA: 'Washington (WA)',
  US_WV: 'West Virginia (WV)',
  US_WI: 'Wisconsin (WI)',
  US_WY: 'Wyoming (WY)',
  // Canada federal + every province/territory, keyed by `CA_<2-letter>`.
  CA_CRA: 'CRA (Federal)',
  CA_AB: 'Alberta (AB)',
  CA_BC: 'British Columbia (BC)',
  CA_MB: 'Manitoba (MB)',
  CA_NB: 'New Brunswick (NB)',
  CA_NL: 'Newfoundland and Labrador (NL)',
  CA_NS: 'Nova Scotia (NS)',
  CA_NT: 'Northwest Territories (NT)',
  CA_NU: 'Nunavut (NU)',
  CA_ON: 'Ontario (ON)',
  CA_PE: 'Prince Edward Island (PE)',
  CA_QC: 'Quebec (QC)',
  CA_SK: 'Saskatchewan (SK)',
  CA_YT: 'Yukon (YT)',
};

export function toTaxPaymentUi(response: TaxPaymentResponse): TaxPaymentUi {
  return {
    ...response,
    canPayNow: PAYABLE_STATUSES.has(response.displayStatus),
    amount: (response.amountCents ?? 0) / 100,
    statusLabel: STATUS_LABELS[response.displayStatus] ?? response.displayStatus,
    badgeVariant: BADGE_VARIANTS[response.displayStatus] ?? 'muted',
    // Prefer a server-provided label if the response ever gains one, then our
    // local map, then the raw code so unknown authorities still render.
    taxAuthorityLabel:
      (response as { taxAuthorityLabel?: string }).taxAuthorityLabel
      ?? TAX_AUTHORITY_LABELS[response.taxAuthority]
      ?? response.taxAuthority,
  };
}
